import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import * as turf from "@turf/turf";
import { previewUrlFor, fetchAuthedBlob } from "./api";

const BASEMAPS = {
  dark: {
    label: "Dark",
    tiles: [
      "https://cartodb-basemaps-a.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png",
      "https://cartodb-basemaps-b.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png",
      "https://cartodb-basemaps-c.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png",
    ],
    attribution: "© OpenStreetMap, © CARTO",
  },
  satellite: {
    label: "Satellite",
    tiles: [
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    ],
    attribution: "Esri, Maxar, Earthstar Geographics",
  },
  topo: {
    label: "Topographic",
    tiles: ["https://a.tile.opentopomap.org/{z}/{x}/{y}.png", "https://b.tile.opentopomap.org/{z}/{x}/{y}.png"],
    attribution: "© OpenTopoMap (CC-BY-SA)",
  },
  osm: {
    label: "OpenStreetMap",
    tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
    attribution: "© OpenStreetMap contributors",
  },
};

const buildStyle = (key) => {
  const bm = BASEMAPS[key];
  return {
    version: 8,
    glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
    sources: {
      "tbg-base": {
        type: "raster",
        tiles: bm.tiles,
        tileSize: 256,
        attribution: bm.attribution,
        maxzoom: 19,
      },
    },
    layers: [
      { id: "tbg-base", type: "raster", source: "tbg-base", paint: { "raster-opacity": 1 } },
    ],
  };
};

export default function Map2D({ dataset, basemap, overlayOpacity, measureMode, onCoord }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const overlayUrlRef = useRef(null);
  const [measure, setMeasure] = useState({ pts: [], distM: 0, areaM2: 0 });

  // Initialize map.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildStyle(basemap),
      center: [-97.2, 49.93],
      zoom: 9,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");
    map.on("mousemove", (e) => onCoord?.([e.lngLat.lng, e.lngLat.lat]));
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Basemap switch.
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    map.setStyle(buildStyle(basemap));
    map.once("styledata", () => addOverlay(dataset));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basemap]);

  // Overlay + fit bounds when dataset changes.
  useEffect(() => {
    addOverlay(dataset);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset?.id]);

  const addCallIdRef = useRef(0);

  const addOverlay = async (ds) => {
    const map = mapRef.current; if (!map || !ds) return;
    const myCallId = ++addCallIdRef.current;
    // Remove old overlay (layer + source) — only after any in-flight call is
    // cancelled, to avoid the "source already exists" race when basemap and
    // dataset change concurrently.
    if (map.getLayer("tbg-overlay")) map.removeLayer("tbg-overlay");
    if (map.getSource("tbg-overlay")) map.removeSource("tbg-overlay");
    if (overlayUrlRef.current) {
      URL.revokeObjectURL(overlayUrlRef.current);
      overlayUrlRef.current = null;
    }
    if (ds.kind !== "geotiff" || !ds.bounds) return;
    try {
      const url = await fetchAuthedBlob(previewUrlFor(ds.id));
      // If a newer call happened during await, bail.
      if (myCallId !== addCallIdRef.current) {
        URL.revokeObjectURL(url);
        return;
      }
      // Defensive: source may have been re-added by the newer call; clear if so.
      if (map.getLayer("tbg-overlay")) map.removeLayer("tbg-overlay");
      if (map.getSource("tbg-overlay")) map.removeSource("tbg-overlay");
      overlayUrlRef.current = url;
      const [w, s, e, n] = ds.bounds;
      map.addSource("tbg-overlay", {
        type: "image",
        url,
        coordinates: [[w, n], [e, n], [e, s], [w, s]],
      });
      map.addLayer({
        id: "tbg-overlay",
        type: "raster",
        source: "tbg-overlay",
        paint: { "raster-opacity": overlayOpacity ?? 0.85 },
      });
      // Fit bounds.
      map.fitBounds([[w, s], [e, n]], { padding: 60, duration: 700 });
    } catch (err) {
      console.error("overlay failed", err);
    }
  };

  // Update overlay opacity.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer("tbg-overlay")) return;
    map.setPaintProperty("tbg-overlay", "raster-opacity", overlayOpacity);
  }, [overlayOpacity]);

  // ---- Measurement -------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current; if (!map) return;

    const clickHandler = (e) => {
      if (!measureMode) return;
      setMeasure((m) => {
        const pts = [...m.pts, [e.lngLat.lng, e.lngLat.lat]];
        return computeMetrics(pts, measureMode);
      });
    };
    map.on("click", clickHandler);
    return () => { map.off("click", clickHandler); };
  }, [measureMode]);

  // Reset measurement when mode toggles off or changes mode/dataset.
  useEffect(() => { setMeasure({ pts: [], distM: 0, areaM2: 0 }); }, [measureMode, dataset?.id]);

  // Draw measurement layer.
  useEffect(() => {
    const map = mapRef.current; if (!map || !map.isStyleLoaded()) return;
    const srcId = "tbg-measure";
    const featureCollection = buildMeasureFeatures(measure.pts, measureMode);
    if (map.getSource(srcId)) {
      map.getSource(srcId).setData(featureCollection);
    } else {
      map.addSource(srcId, { type: "geojson", data: featureCollection });
      map.addLayer({
        id: "tbg-measure-fill", type: "fill", source: srcId,
        filter: ["==", "$type", "Polygon"],
        paint: { "fill-color": "#3dc0af", "fill-opacity": 0.18 },
      });
      map.addLayer({
        id: "tbg-measure-line", type: "line", source: srcId,
        filter: ["any", ["==", "$type", "LineString"], ["==", "$type", "Polygon"]],
        paint: { "line-color": "#3dc0af", "line-width": 2 },
      });
      map.addLayer({
        id: "tbg-measure-pts", type: "circle", source: srcId,
        filter: ["==", "$type", "Point"],
        paint: {
          "circle-radius": 4,
          "circle-color": "#3dc0af",
          "circle-stroke-color": "#051210",
          "circle-stroke-width": 2,
        },
      });
    }
  }, [measure, measureMode]);

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%" }} data-testid="viewer-map-2d">
      <div className="tbg-vw-hud bl" style={{ bottom: 36 }}>
        {measureMode && (
          <div className="chip" data-testid="viewer-measure-readout">
            <span className="k">{measureMode === "distance" ? "Distance" : "Area"}</span>
            <span className="v">
              {measureMode === "distance"
                ? (measure.distM >= 1000 ? `${(measure.distM / 1000).toFixed(3)} km` : `${measure.distM.toFixed(1)} m`)
                : (measure.areaM2 >= 1e6 ? `${(measure.areaM2 / 1e6).toFixed(3)} km²` : `${measure.areaM2.toFixed(0)} m²`)}
            </span>
            <span className="k" style={{ marginLeft: 8 }}>Pts {measure.pts.length}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function computeMetrics(pts, mode) {
  if (mode === "distance" && pts.length >= 2) {
    const line = turf.lineString(pts);
    const distM = turf.length(line, { units: "kilometers" }) * 1000;
    return { pts, distM, areaM2: 0 };
  }
  if (mode === "area" && pts.length >= 3) {
    const ring = [...pts, pts[0]];
    const poly = turf.polygon([ring]);
    const areaM2 = turf.area(poly);
    return { pts, distM: 0, areaM2 };
  }
  return { pts, distM: 0, areaM2: 0 };
}

function buildMeasureFeatures(pts, mode) {
  const feats = pts.map((c) => ({ type: "Feature", geometry: { type: "Point", coordinates: c }, properties: {} }));
  if (mode === "distance" && pts.length >= 2) {
    feats.push({ type: "Feature", geometry: { type: "LineString", coordinates: pts }, properties: {} });
  } else if (mode === "area" && pts.length >= 3) {
    feats.push({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [[...pts, pts[0]]] },
      properties: {},
    });
  }
  return { type: "FeatureCollection", features: feats };
}
